import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellRing, AlertTriangle, Eye, Rocket, ShieldCheck, CircleSlash, Info } from 'lucide-react';
import { api } from '../api.js';
import { Screen, Section, Rows, Toggle, EmptyState, ago, Btn } from '../ui/kit.js';
import { reporter } from '../pwa/report.js';

interface Notif {
  id: string;
  ts: string;
  event: string;
  title: string;
  body: string;
  job_id: string | null;
  url: string | null;
  read_at: string | null;
}

const ICONS: Record<string, { icon: typeof Info; tone: string }> = {
  needs_input: { icon: BellRing, tone: 'var(--ops-accent)' },
  ci_red: { icon: AlertTriangle, tone: 'var(--ops-bad)' },
  errored: { icon: AlertTriangle, tone: 'var(--ops-bad)' },
  review_ready: { icon: Eye, tone: 'var(--ops-hold)' },
  merging: { icon: Rocket, tone: 'var(--ops-run)' },
  merged: { icon: Rocket, tone: 'var(--ops-ok)' },
  promotable: { icon: ShieldCheck, tone: 'var(--ops-ok)' },
  stopped: { icon: CircleSlash, tone: 'var(--ops-wait)' },
};

const EVENT_LABELS: Record<string, string> = {
  needs_input: 'The session needs me',
  self_verify_failed: 'Self-verify failed',
  review_ready: 'Ready for review',
  merging: 'Merging',
  merged: 'Merged to main',
  promotable: 'Promotable on staging',
  ci_red: 'CI went red',
  stopped: 'Job stopped',
  errored: 'Job errored',
  rebase_new_cycle: 'Rebased, new cycle',
  stalled: 'Session stalled',
  interrupted: 'Session interrupted',
  artifacts_ready: 'Workshop delivered',
  digest: 'Morning digest',
  system: 'Everything else',
};

const PAGE = 25;

export function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notif[]>([]);
  const [more, setMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<{ event: string; enabled: boolean }[]>([]);

  const load = () => {
    void api
      .get<{ notifications: Notif[]; more: boolean; cursor: string | null }>(`/api/notifications?limit=${PAGE}`)
      .then((r) => {
        setItems(r.notifications);
        setMore(r.more);
        setCursor(r.cursor);
      });
    void api
      .get<{ prefs: { event: string; enabled: boolean }[] }>('/api/push/prefs')
      .then((r) => setPrefs(r.prefs))
      .catch(reporter('your notification settings'));
  };
  const loadMore = () => {
    if (!cursor) return;
    void api
      .get<{ notifications: Notif[]; more: boolean; cursor: string | null }>(
        `/api/notifications?limit=${PAGE}&before=${encodeURIComponent(cursor)}`,
      )
      .then((r) => {
        setItems((x) => [...x, ...r.notifications]);
        setMore(r.more);
        setCursor(r.cursor);
      });
  };
  useEffect(load, []);

  const unread = items.filter((n) => !n.read_at).length;

  return (
    <Screen
      title="Notifications"
      sub={unread ? `${unread} unread` : 'All read'}
      actions={
        unread ? (
          <Btn kind="ghost" onClick={() => void api.post('/api/notifications/read').then(load).catch(reporter('marking them read'))}>
            Mark all read
          </Btn>
        ) : undefined
      }
    >
      <Rows>
        {items.length === 0 ? (
          <EmptyState title="Quiet so far.">Job events land here and on the lock screen.</EmptyState>
        ) : (
          items.map((n) => {
            const meta = ICONS[n.event] ?? { icon: Info, tone: 'var(--ops-ink-subtle)' };
            return (
              <button
                key={n.id}
                type="button"
                className={`ops-notif${n.read_at ? '' : ' ops-notif--unread'}`}
                onClick={() => {
                  void api.post('/api/notifications/read', { ids: [n.id] }).then(load).catch(reporter('marking it read'));
                  if (n.url) {
                    const url = new URL(n.url, window.location.origin);
                    if (url.origin === window.location.origin) navigate(url.pathname);
                    else window.open(n.url, '_blank');
                  }
                }}
              >
                <span className="ops-notif__icon" style={{ background: 'var(--ops-surface-2)', color: meta.tone }}>
                  <meta.icon />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div className="ops-notif__title">{n.title}</div>
                  <div className="ops-notif__body">{n.body}</div>
                </span>
                <span className="ops-notif__when">{ago(n.ts)}</span>
              </button>
            );
          })
        )}
      </Rows>
      {more ? (
        <button type="button" className="ops-more" onClick={loadMore} data-testid="notifications-more">
          Show more
        </button>
      ) : null}

      <Section title="What reaches the phone">
        <Rows>
          {prefs.map((p) => (
            <div key={p.event} className="ops-row">
              <div className="ops-row__main">
                <div className="ops-row__title" style={{ fontSize: 14 }}>
                  {EVENT_LABELS[p.event] ?? p.event}
                </div>
              </div>
              <Toggle
                checked={p.enabled}
                label={p.event}
                onChange={(v) => {
                  setPrefs((ps) => ps.map((x) => (x.event === p.event ? { ...x, enabled: v } : x)));
                  // The toggle flips optimistically; if the save fails, say so rather
                  // than leaving a switch that lies (V6 section J).
                  void api.put('/api/push/prefs', { event: p.event, enabled: v }).catch(reporter('the notification setting'));
                }}
              />
            </div>
          ))}
        </Rows>
        <p className="ops-hint">Everything still lands in this list; the toggles only gate the push.</p>
      </Section>
    </Screen>
  );
}
